/** "Add notification" dialog (console counterpart of the dashboard's
 *  AddNewNotification): tenant (TenantPicker with the System sentinel,
 *  prefilled from the grid's current selection), name, event (autocomplete
 *  over the event catalog, freeSolo so ops can type a custom code), override
 *  mode, and — for mode 2 on a real tenant — the tour variants. POSTs the
 *  minimal create body; delivery methods / composition are added on the edit
 *  page the dialog navigates to.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { ConsoleApiError } from '@/api/client';
import * as nfApi from '@/api/notifications';
import { TOUR_OVERRIDE_MODE_LABELS, type NotificationEvent } from '@/api/notifications';
import * as toursApi from '@/api/tours';
import { AppDialog } from '@/components/AppDialog';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { singlePageTenant } from '@/scope/pageTenants';

/** The "System / global" tenant sentinel (tour_operator IS NULL records) —
 *  shared with the notifications grid's picker. */
export const SYSTEM_TENANT: TenantPickerValue = { id: 0, name: 'System / global (no operator)' };

const TOUR_OVERRIDE_DEFAULT = 1;
const TOUR_SPECIFIC_MODE = 2;

const FormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(256),
  event: z.string().trim().min(1, 'Event is required').max(128),
  tour_override_mode: z.number().int().min(0).max(2),
});

type FormValues = z.infer<typeof FormSchema>;

interface TourOption {
  id: number;
  label: string;
}

function tourLabel(t: toursApi.TourListItem): string {
  const dates = t.is_bespoke
    ? `${t.start_date ?? '?'} – ${t.end_date ?? '?'}`
    : 'recurring';
  return `#${t.id} ${t.name} (${dates})`;
}

interface Props {
  open: boolean;
  /** The page's operators (D9): the one is pre-selected, all are featured; see
   *  `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
  /** Open on System / global: the page is filtered to the `system` sentinel,
   *  which names no operator (so it can't travel in `pageTenants`). */
  defaultSystem?: boolean;
  /** Prefill the event code — the timeline opens this dialog from a specific
   *  moment on the guest journey, so the event is already decided. */
  defaultEvent?: string;
  onClose: () => void;
}

export function CreateNotificationDialog({
  open,
  pageTenants,
  defaultSystem = false,
  defaultEvent,
  onClose,
}: Props) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [tenant, setTenant] = useState<TenantPickerValue | null>(null);
  const [tours, setTours] = useState<TourOption[]>([]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: '', event: defaultEvent ?? '', tour_override_mode: TOUR_OVERRIDE_DEFAULT },
  });

  useEffect(() => {
    if (!open) return;
    reset({ name: '', event: defaultEvent ?? '', tour_override_mode: TOUR_OVERRIDE_DEFAULT });
    setTenant(defaultSystem ? SYSTEM_TENANT : singlePageTenant(pageTenants));
    setTours([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultEvent]);

  const eventsQuery = useQuery({
    queryKey: ['nf-events'],
    queryFn: () => nfApi.listEvents(),
    staleTime: 30 * 60_000,
    enabled: open,
  });
  const eventOptions = eventsQuery.data?.events ?? [];

  const overrideMode = watch('tour_override_mode');
  const isSystem = tenant?.id === SYSTEM_TENANT.id;
  const realTenantId = tenant && !isSystem ? tenant.id : null;
  const showTours = overrideMode === TOUR_SPECIFIC_MODE && realTenantId != null;

  // Tours belong to a tenant — clear the picks when the tenant changes.
  const handleTenantChange = (t: TenantPickerValue | null) => {
    setTenant(t);
    setTours([]);
  };

  const createMutation = useMutation({
    mutationFn: (body: nfApi.NotificationCreateInput) => nfApi.create(body),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      snackbar.show(`Notification #${result.id} created.`, 'success');
      onClose();
      navigate(`/notifications/${result.id}`);
    },
    onError: (e) => {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Create failed.', 'error');
    },
  });

  const submit = handleSubmit((v) => {
    if (!tenant) return;
    createMutation.mutate({
      tour_operator: isSystem ? 'system' : tenant.id,
      name: v.name.trim(),
      event: v.event.trim(),
      tour_override_mode: v.tour_override_mode,
      ...(showTours && tours.length > 0 ? { tours: tours.map((t) => t.id) } : {}),
    });
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="New notification"
      size="sm"
      disableBackdropClose
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!tenant || createMutation.isPending}
            onClick={submit}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        onSubmit={submit}
        noValidate
        sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <Box>
          <TenantPicker
            value={tenant}
            featured={pageTenants}
            onChange={handleTenantChange}
            extraOptions={[SYSTEM_TENANT]}
            placeholder="Pick an operator or System / global"
          />
          {!tenant && (
            <Typography variant="caption" color="text.secondary">
              Required — pick the owning operator, or System / global for a system template.
            </Typography>
          )}
        </Box>

        <TextField
          {...register('name')}
          label="Name"
          size="small"
          fullWidth
          required
          error={Boolean(errors.name)}
          helperText={errors.name?.message}
        />

        <Controller
          control={control}
          name="event"
          render={({ field }) => (
            <Autocomplete<NotificationEvent | string, false, false, true>
              freeSolo
              size="small"
              options={eventOptions}
              loading={eventsQuery.isLoading}
              value={field.value}
              onChange={(_, v) => field.onChange(typeof v === 'string' ? v : (v?.code ?? ''))}
              onInputChange={(_, v, reason) => {
                if (reason === 'input' || reason === 'clear') field.onChange(v);
              }}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.code)}
              renderOption={(props, o) => {
                const { key, ...optionProps } = props as { key?: string } & typeof props;
                const opt = o as NotificationEvent;
                return (
                  <Box component="li" key={key ?? opt.code} {...optionProps}>
                    <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
                      {opt.code}
                    </Box>
                    {opt.label && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 0.75 }}
                      >
                        {opt.label}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Event"
                  required
                  error={Boolean(errors.event)}
                  helperText={errors.event?.message ?? 'Pick from the catalog or type a custom event code.'}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="tour_override_mode"
          render={({ field }) => (
            <TextField
              select
              size="small"
              label="Override mode"
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value))}
            >
              {Object.entries(TOUR_OVERRIDE_MODE_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={Number(value)}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {overrideMode === TOUR_SPECIFIC_MODE && !showTours && (
          <Typography variant="caption" color="text.secondary">
            Pick a real tenant above to choose the tours this override applies to.
          </Typography>
        )}

        {showTours && (
          <Stack spacing={0.5}>
            <RemoteAutocomplete<TourOption>
              size="small"
              label="Tours"
              multiple
              queryFn={(q) =>
                toursApi
                  .list({ tour_operator_id: realTenantId, search: q, pageSize: 20 })
                  .then((r) => r.results.map((t) => ({ id: t.id, label: tourLabel(t) })))
              }
              getOptionLabel={(t) => t.label}
              getOptionKey={(t) => t.id}
              value={tours}
              onChange={(v) => setTours((v as TourOption[] | null) ?? [])}
              fetchOnEmpty
            />
            <Typography variant="caption" color="text.secondary">
              The tour-specific variant fires only for these tours.
            </Typography>
          </Stack>
        )}
      </Box>
    </AppDialog>
  );
}
